import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { logisticsApi } from '../../network/apis/logistics';
import { Bell, Truck, Store, Orders, Products } from '../../assets/icons';
import '../../assets/css/logistics/dashboard.css';

const LogisticsDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState(null);
    const [recentShipments, setRecentShipments] = useState([]);

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            
            const [dashboardResponse, shipmentsResponse] = await Promise.all([
                logisticsApi.getDashboard(),
                logisticsApi.getShipments({ limit: 5 })
            ]);
            
            const dashboard = dashboardResponse.data?.data || dashboardResponse.data;
            const shipments = shipmentsResponse.data?.data || shipmentsResponse.data || [];
            
            setDashboardData(dashboard);
            setRecentShipments(Array.isArray(shipments) ? shipments.slice(0, 5) : []);
        } catch (err) {
            console.error('Failed to fetch dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const getStatusClass = (status) => {
        return status?.toLowerCase().replace(' ', '_') || 'pending';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="logistics-dashboard">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="logistics-dashboard">
                <div className="error-state">
                    <p>{error}</p>
                    <button onClick={fetchDashboardData}>Retry</button>
                </div>
            </div>
        );
    }

    const company = dashboardData?.company || {};
    const statistics = dashboardData?.statistics || {};
    const companyName = company.company_name || 'Your Company';
    const isVerified = company.is_verified;

    return (
        <div className="logistics-dashboard">
            <header className="dashboard-header">
                <div className="welcome-section">
                    <h1>Welcome, {companyName}</h1>
                    <p>Manage your deliveries and track shipments</p>
                </div>
                
                <div className="header-actions">
                    <button className="notification-btn">
                        <img src={Bell} alt="" />
                        {statistics.pending_shipments > 0 && (
                            <span className="badge">{statistics.pending_shipments}</span>
                        )}
                    </button>
                    <Link to="/logistics/profile" className="profile-preview">
                        <img src="/logistics-profile.jpg" alt="Company" onError={(e) => e.target.src = '/default-avatar.png'} />
                    </Link>
                </div>
            </header>

            {!isVerified && (
                <div className="verification-banner">
                    <span className="banner-icon">⚠️</span>
                    <div className="banner-content">
                        <h4>Verification Pending</h4>
                        <p>Your company is under review. You can access limited features until verified.</p>
                    </div>
                </div>
            )}

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Shipments</h3>
                    <div className="stat-value">{statistics.total_shipments || 0}</div>
                    <span className="stat-label">{statistics.pending_shipments || 0} pending</span>
                </div>
                <div className="stat-card highlight">
                    <h3>In Transit</h3>
                    <div className="stat-value">{statistics.in_transit_shipments || 0}</div>
                    <span className="stat-label">Active deliveries</span>
                </div>
                <div className="stat-card">
                    <h3>Delivered</h3>
                    <div className="stat-value">{statistics.delivered_shipments || 0}</div>
                    <span className="stat-label">Completed</span>
                </div>
                <div className="stat-card">
                    <h3>Available Vehicles</h3>
                    <div className="stat-value">{statistics.available_vehicles || 0}</div>
                    <span className="stat-label">of {statistics.total_vehicles || 0} total</span>
                </div>
            </div>

            <div className="dashboard-content">
                <main className="main-section">
                    <section className="shipments-section">
                        <div className="section-header">
                            <h2>Recent Shipments</h2>
                            <Link to="/logistics/shipments" className="view-all">
                                View All Shipments
                            </Link>
                        </div>
                        
                        <div className="shipments-list">
                            {recentShipments.length === 0 ? (
                                <div className="empty-state">
                                    <img src={Truck} alt="" />
                                    <p>No shipments yet</p>
                                </div>
                            ) : (
                                recentShipments.map(shipment => (
                                    <Link 
                                        key={shipment.id} 
                                        to={`/logistics/shipments/${shipment.id}`}
                                        className="shipment-card"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <div className="shipment-header">
                                            <span className="tracking-number">
                                                {shipment.tracking_number || `#${shipment.id}`}
                                            </span>
                                            <span className={`shipment-status ${getStatusClass(shipment.status)}`}>
                                                {shipment.status_display || shipment.status}
                                            </span>
                                        </div>
                                        <div className="shipment-details">
                                            <div>
                                                <span className="detail-label">Customer:</span>
                                                <span className="detail-value"> {shipment.order?.customer_name || 'N/A'}</span>
                                            </div>
                                            <div>
                                                <span className="detail-label">Date:</span>
                                                <span className="detail-value"> {formatDate(shipment.created_at)}</span>
                                            </div>
                                        </div>
                                        {(shipment.pickup_address || shipment.delivery_address) && (
                                            <div className="shipment-route">
                                                <div className="from">
                                                    <strong>From:</strong> {shipment.pickup_address?.city || 'N/A'}
                                                </div>
                                                <span className="arrow">→</span>
                                                <div className="to">
                                                    <strong>To:</strong> {shipment.delivery_address?.city || 'N/A'}
                                                </div>
                                            </div>
                                        )}
                                    </Link>
                                ))
                            )}
                        </div>
                    </section>

                    <section className="rates-section">
                        <div className="section-header">
                            <h2>Active Rates</h2>
                            <Link to="/logistics/rates" className="view-all">
                                Manage Rates
                            </Link>
                        </div>
                        <div className="rates-summary">
                            <p>{statistics.active_rates || 0} active shipping rates configured</p>
                        </div>
                    </section>
                </main>

                <aside className="dashboard-sidebar">
                    <div className="company-panel">
                        <h3>Company Info</h3>
                        <div className="company-info">
                            <div className="info-row">
                                <span className="info-label">Status</span>
                                <span className="info-value">
                                    {isVerified ? '✅ Verified' : '⏳ Pending'}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Rating</span>
                                <div className="rating-display">
                                    <span className="star">★</span>
                                    <span className="info-value">{statistics.rating?.toFixed(1) || '0.0'}</span>
                                </div>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Vehicles</span>
                                <span className="info-value">{statistics.total_vehicles || 0}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Active Rates</span>
                                <span className="info-value">{statistics.active_rates || 0}</span>
                            </div>
                        </div>
                    </div>

                    <nav className="quick-actions">
                        <h3>Quick Actions</h3>
                        <Link to="/logistics/shipments" className="action-btn">
                            <img src={Truck} alt="" />
                            View Shipments
                        </Link>
                        <Link to="/logistics/rates" className="action-btn">
                            <img src={Store} alt="" />
                            Manage Rates
                        </Link>
                        <Link to="/logistics/vehicles" className="action-btn">
                            <img src={Orders} alt="" />
                            Manage Vehicles
                        </Link>
                        <Link to="/logistics/profile" className="action-btn">
                            <img src={Products} alt="" />
                            Edit Profile
                        </Link>
                    </nav>
                </aside>
            </div>
        </div>
    );
};

export default LogisticsDashboard;
